import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Avatar,
  Chip,
  Button,
  Skeleton,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsMartialArtsIcon from "@mui/icons-material/SportsMartialArts";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import QuizIcon from "@mui/icons-material/Quiz";
import BoltIcon from "@mui/icons-material/Bolt";
import { useNavigate, useMatch, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetMyLeaderboardStatsQuery,
  useGetWeeklyLeaderboardQuery,
} from "../../../../../app/leaderboardApiSlice";
import { useGetMyTournamentsQuery } from "../../../../../app/tournamentApiSlice";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Mode config ──────────────────────────────────────────────────────────────
const MODES = [
  {
    id: "practice",
    label: "Practice",
    sublabel: "Solo Challenge",
    tagline: "AI-generated quiz, built just for you",
    icon: SchoolIcon,
    bg: "#16a34a",
    bgHover: "#15803d",
    glow: "rgba(22,163,74,0.45)",
    emoji: "🎯",
    animDelay: "0s",
  },
  {
    id: "duel",
    label: "Duel",
    sublabel: "1v1 Battle",
    tagline: "Challenge a classmate in real time",
    icon: SportsMartialArtsIcon,
    bg: "#4f46e5",
    bgHover: "#4338ca",
    glow: "rgba(79,70,229,0.45)",
    emoji: "⚔️",
    animDelay: "0.08s",
  },
  {
    id: "tournament",
    label: "Tournament",
    sublabel: "Championship",
    tagline: "Compete with many students & win",
    icon: EmojiEventsIcon,
    bg: "#d97706",
    bgHover: "#b45309",
    glow: "rgba(217,119,6,0.45)",
    emoji: "🏆",
    animDelay: "0.16s",
  },
];

const PRACTICE_MODES = [
  {
    id: "quiz",
    label: "Quiz Mode",
    description: "Test yourself with AI questions",
    icon: QuizIcon,
    bg: "#0369a1",
    bgHover: "#075985",
    glow: "rgba(3,105,161,0.45)",
    route: "practice/quiz",
  },
  {
    id: "learn",
    label: "Learn Mode",
    description: "Read a lesson, watch a video, then quiz",
    icon: AutoStoriesIcon,
    bg: "#7c3aed",
    bgHover: "#6d28d9",
    glow: "rgba(124,58,237,0.45)",
    route: "practice/learn",
  },
];

// ─── Medal colours ────────────────────────────────────────────────────────────
const MEDAL = ["🥇", "🥈", "🥉"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

const getUserName = (user) => {
  if (!user) return "—";
  const p = user.auth_profile;
  if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return user.username || user.schoola_id || "—";
};

// ─── Mode Card ────────────────────────────────────────────────────────────────
const ModeCard = ({ mode, onClick, delay = "0s" }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = mode.icon;
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: hovered ? mode.bgHover : mode.bg,
        borderRadius: "24px",
        p: { xs: "28px 20px", sm: "32px 24px" },
        textAlign: "center",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
        boxShadow: hovered
          ? `0 20px 48px ${mode.glow}, 0 4px 16px rgba(0,0,0,0.5)`
          : `0 6px 24px rgba(0,0,0,0.45)`,
        animation: `${fadeUp} 0.45s ${delay} cubic-bezier(0.22,1,0.36,1) both`,
        userSelect: "none",
      }}
    >
      {/* Accent glow blob */}
      <Box sx={{ position: "absolute", top: "-20%", right: "-10%", width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.12)", pointerEvents: "none" }} />

      {/* Emoji top-right badge */}
      <Box sx={{ position: "absolute", top: 16, right: 18, fontSize: "1.6rem", lineHeight: 1, opacity: 0.9 }}>
        {mode.emoji}
      </Box>

      {/* Icon circle */}
      <Box
        sx={{
          width: { xs: 64, sm: 76 }, height: { xs: 64, sm: 76 },
          borderRadius: "50%", bgcolor: "rgba(255,255,255,0.22)",
          display: "flex", alignItems: "center", justifyContent: "center",
          mx: "auto", mb: 2, border: "2px solid rgba(255,255,255,0.3)",
        }}
      >
        <Icon sx={{ fontSize: { xs: "2rem", sm: "2.4rem" }, color: "#fff" }} />
      </Box>

      {/* Sub-label chip */}
      <Box sx={{ display: "inline-block", bgcolor: "rgba(255,255,255,0.22)", borderRadius: "6px", px: 1, py: 0.2, mb: 0.8 }}>
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#fff", letterSpacing: "2px", textTransform: "uppercase" }}>
          {mode.sublabel}
        </Typography>
      </Box>

      {/* Main label */}
      <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", sm: "1.75rem" }, color: "#fff", letterSpacing: "0.5px", lineHeight: 1.1, mb: 0.6, textShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>
        {mode.label}
      </Typography>

      {/* Tagline */}
      <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.82)", fontWeight: 500, lineHeight: 1.4 }}>
        {mode.tagline}
      </Typography>
    </Box>
  );
};

// ─── Practice Sub-Card ────────────────────────────────────────────────────────
const PracticeSubCard = ({ mode, onClick, delay = "0s" }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = mode.icon;
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: hovered ? mode.bgHover : mode.bg,
        borderRadius: "20px",
        p: { xs: "24px 20px", sm: "28px 24px" },
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-5px) scale(1.02)" : "translateY(0) scale(1)",
        boxShadow: hovered ? `0 16px 40px ${mode.glow}, 0 4px 12px rgba(0,0,0,0.4)` : "0 4px 16px rgba(0,0,0,0.4)",
        animation: `${fadeUp} 0.4s ${delay} cubic-bezier(0.22,1,0.36,1) both`,
        userSelect: "none",
      }}
    >
      <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5, border: "2px solid rgba(255,255,255,0.28)" }}>
        <Icon sx={{ fontSize: "1.8rem", color: "#fff" }} />
      </Box>
      <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", color: "#fff", mb: 0.4, textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>
        {mode.label}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.82)", fontWeight: 500 }}>
        {mode.description}
      </Typography>
    </Box>
  );
};

// ─── Leaderboard Row ──────────────────────────────────────────────────────────
const LeaderRow = ({ entry, rank }) => {
  const name = getUserName(entry.user);
  const medal = rank < 3 ? MEDAL[rank] : null;
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: "10px 14px",
        borderRadius: "14px",
        bgcolor: rank === 0 ? "rgba(234,179,8,0.1)" : "rgba(255,255,255,0.05)",
        border: rank === 0 ? "1.5px solid rgba(234,179,8,0.4)" : "1px solid rgba(255,255,255,0.08)",
        animation: `${fadeUp} 0.4s ${rank * 0.06}s ease both`,
      }}
    >
      <Typography sx={{ fontWeight: 900, fontSize: "1rem", minWidth: 24, textAlign: "center" }}>
        {medal || <Typography component="span" sx={{ fontSize: "0.82rem", fontWeight: 700, color: "rgba(255,255,255,0.3)" }}>#{rank + 1}</Typography>}
      </Typography>
      <Avatar sx={{ width: 32, height: 32, fontSize: "0.75rem", fontWeight: 700, bgcolor: rank === 0 ? "#ca8a04" : "rgba(99,102,241,0.7)" }}>
        {getInitials(name)}
      </Avatar>
      <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.85rem", color: "rgba(255,255,255,0.9)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
        <BoltIcon sx={{ fontSize: "0.9rem", color: "#f59e0b" }} />
        <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "rgba(255,255,255,0.6)" }}>
          {entry.points ?? 0}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Tournament Chip ──────────────────────────────────────────────────────────
const TournamentChip = ({ t, onNavigate }) => (
  <Box
    onClick={() => onNavigate(t.id)}
    sx={{
      display: "inline-flex", alignItems: "center", gap: 1,
      px: 1.5, py: 1, borderRadius: "14px",
      bgcolor: t.status === "active" ? "rgba(251,146,60,0.12)" : "rgba(255,255,255,0.06)",
      border: t.status === "active" ? "1.5px solid rgba(251,146,60,0.5)" : "1px solid rgba(255,255,255,0.12)",
      cursor: "pointer", flexShrink: 0,
      transition: "all 0.18s",
      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.35)" },
    }}
  >
    <Typography sx={{ fontSize: "1rem" }}>🏆</Typography>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.2, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {t.name}
      </Typography>
      {t.status === "active" && (
        <Typography sx={{ fontSize: "0.65rem", color: "#fb923c", fontWeight: 600 }}>● LIVE</Typography>
      )}
    </Box>
  </Box>
);

// ─── Section Label ────────────────────────────────────────────────────────────
const SectionLabel = ({ icon, label, delay = "0s" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, animation: `${fadeUp} 0.4s ${delay} ease both` }}>
    <Typography sx={{ fontSize: "1rem" }}>{icon}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", letterSpacing: "2px", textTransform: "uppercase" }}>
      {label}
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const GameHub = () => {
  const navigate = useNavigate();
  const [showPractice, setShowPractice] = useState(false);
  const isHub = useMatch("/lms/student/quiz/play");
  const showModeCards = !!isHub;

  const authData = useSelector((state) => state.auth);
  const myName = authData?.user?.name || authData?.user?.username || "Student";

  const { data: statsData, isLoading: statsLoading } = useGetMyLeaderboardStatsQuery();
  const { data: leaderboardData, isLoading: lbLoading } = useGetWeeklyLeaderboardQuery();
  const { data: tournamentsData } = useGetMyTournamentsQuery();

  const stats = statsData?.stats || statsData || {};
  // leaderboardData is now paginated: { items, total, page, pages, has_next }
  const leaderboard = Array.isArray(leaderboardData)
    ? leaderboardData
    : (leaderboardData?.items || leaderboardData?.leaderboard || []);
  const top5 = leaderboard.slice(0, 5);
  const tournaments = tournamentsData?.tournaments || [];
  const activeTournaments = tournaments.filter((t) => t.status === "active" || t.status === "open");

  const handleExit = () => navigate("/lms/student/quiz");

  const handleModeClick = (modeId) => {
    if (modeId === "practice") setShowPractice(true);
    else if (modeId === "duel") navigate("duel");
    else if (modeId === "tournament") navigate("tournament");
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        overflowY: "auto",
        background: "linear-gradient(160deg, #060d1a 0%, #0c1929 50%, #060d1a 100%)",
      }}
    >
      {/* Dot grid overlay */}
      <Box
        sx={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Exit button — only visible on the hub page, not sub-routes */}
      {showModeCards && (
        <IconButton
          onClick={handleExit}
          sx={{
            position: "fixed", top: 16, right: 16, zIndex: 1401,
            color: "rgba(255,255,255,0.8)",
            bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            backdropFilter: "blur(8px)",
            transition: "all 0.2s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.18)", transform: "scale(1.08)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Sub-route view */}
      {!showModeCards && (
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Outlet />
        </Box>
      )}

      {/* Mode card view */}
      {showModeCards && (
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 960, mx: "auto", px: { xs: 2, sm: 4, md: 6 }, py: 3, pb: 8 }}>

          {/* ── Hero stats bar ── */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #3730a3 55%, #4f46e5 100%)",
              borderRadius: "20px",
              p: { xs: "18px 20px", sm: "20px 24px" },
              mb: 3,
              display: "flex", alignItems: "center", gap: 2,
              animation: `${fadeUp} 0.4s ease both`,
              boxShadow: "0 4px 24px rgba(79,70,229,0.35)",
              border: "1px solid rgba(99,102,241,0.3)",
            }}
          >
            <Avatar
              sx={{
                width: 52, height: 52, fontWeight: 800, fontSize: "1.2rem",
                bgcolor: "rgba(255,255,255,0.18)",
                border: "2px solid rgba(255,255,255,0.35)",
              }}
            >
              {getInitials(myName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#fff", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {myName}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)", mt: 0.2 }}>
                Ready to play?
              </Typography>
            </Box>
            {statsLoading ? (
              <Skeleton variant="rounded" width={90} height={36} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: "12px" }} />
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                {stats.week_rank && (
                  <Box sx={{ textAlign: "center", bgcolor: "rgba(255,255,255,0.12)", borderRadius: "12px", px: 1.5, py: 0.8 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: "1rem", color: "#fff", lineHeight: 1 }}>#{stats.week_rank}</Typography>
                    <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: 0.5 }}>Rank</Typography>
                  </Box>
                )}
                {stats.week_points != null && (
                  <Box sx={{ textAlign: "center", bgcolor: "rgba(255,255,255,0.12)", borderRadius: "12px", px: 1.5, py: 0.8 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: "1rem", color: "#fde68a", lineHeight: 1 }}>{stats.week_points}</Typography>
                    <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: 0.5 }}>Pts</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* ── Mode cards / Practice sub-cards ── */}
          {!showPractice ? (
            <>
              <SectionLabel icon="🎮" label="Choose Your Mode" />
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {MODES.map((mode) => (
                  <Grid item xs={12} sm={4} key={mode.id}>
                    <ModeCard mode={mode} onClick={() => handleModeClick(mode.id)} delay={mode.animDelay} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => setShowPractice(false)}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    bgcolor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", letterSpacing: "2px", textTransform: "uppercase" }}>
                  🎯 Choose Practice Mode
                </Typography>
              </Box>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {PRACTICE_MODES.map((mode, idx) => (
                  <Grid item xs={12} sm={6} key={mode.id}>
                    <PracticeSubCard mode={mode} onClick={() => navigate(mode.route)} delay={`${idx * 0.08}s`} />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* ── Leaderboard strip ── */}
          <SectionLabel icon="📊" label="This Week's Leaderboard" delay="0.1s" />
          <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 1 }}>
            {lbLoading
              ? [0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: "14px", bgcolor: "rgba(255,255,255,0.07)" }} />
                ))
              : top5.length
              ? top5.map((entry, i) => <LeaderRow key={entry.id || i} entry={entry} rank={i} />)
              : (
                <Box sx={{ p: 2, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem" }}>No leaderboard data yet</Typography>
                </Box>
              )
            }
          </Box>

          {/* ── Active tournaments strip ── */}
          {activeTournaments.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SectionLabel icon="🏆" label="Your Tournaments" delay="0.15s" />
              <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, "&::-webkit-scrollbar": { display: "none" } }}>
                {activeTournaments.map((t) => (
                  <TournamentChip key={t.id} t={t} onNavigate={(id) => navigate(`tournament/${id}`)} />
                ))}
              </Box>
            </Box>
          )}

          {/* ── Duel quick-join CTA ── */}
          <Box
            onClick={() => navigate("duel")}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              p: "16px 20px", borderRadius: "20px", cursor: "pointer",
              background: "linear-gradient(135deg, #3730a3 0%, #4f46e5 60%, #6366f1 100%)",
              border: "1px solid rgba(99,102,241,0.4)",
              boxShadow: "0 4px 24px rgba(79,70,229,0.35)",
              animation: `${fadeUp} 0.5s 0.25s ease both`,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 32px rgba(79,70,229,0.5)" },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff" }}>
                ⚔️  Up for a 1v1 Duel?
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", mt: 0.25 }}>
                Jump into matchmaking — find an opponent instantly
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={(e) => { e.stopPropagation(); navigate("duel"); }}
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontWeight: 800,
                borderRadius: "12px",
                px: 2.5,
                flexShrink: 0,
                border: "1px solid rgba(255,255,255,0.3)",
                backdropFilter: "blur(8px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                boxShadow: "none",
              }}
            >
              Duel Now
            </Button>
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default GameHub;
